import { useAppSelector } from '@hooks/app/redux';
import { PlusOutlined } from '@icon';
import { searchWorkspaces } from '@services/external/provider.api';
import { IconButton, Table, Typography, useIsAuthorized } from '@ui';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { TAuthority } from 'types/authority.types';
import { IWorkspaceDetail, SearchWorkspaceParams } from 'types/workspace.types';
import { TTableProps } from '../ProviderPatients/ProviderPatients.utils';
import DetailWorkspaceModal from './components/detail-modal/DetailWorkspaceModal';
import { StyledWorkspaceWrapper } from './Departments.styled';
import { INITIAL_SEARCH_PARAMS, INITIAL_TABLE_STATE, useTableColumns } from './Departments.utils';

const EDIT_AUTH: TAuthority[] = ['workspaceManagementEdit'];

const Departments: React.FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const canEdit = useIsAuthorized(EDIT_AUTH);

  const providerId = useAppSelector((state) => state.users.business[0].id);

  const [detailWorkspaceModalOpen, setDetailWorkspaceModalOpen] = useState<string | undefined>();
  const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] = useState<string | undefined>();

  const [searchParams, setSearchParams] = useState<SearchWorkspaceParams>(INITIAL_SEARCH_PARAMS);

  React.useEffect(() => {
    setSearchParams(INITIAL_SEARCH_PARAMS);
  }, [providerId]);

  const { isFetching, data } = useQuery(
    ['searchWorkspaces', providerId, searchParams],
    async () => {
      const { data: axData } = await searchWorkspaces(providerId as string, searchParams);

      return axData.data;
    },
    {
      keepPreviousData: true,
    },
  );

  const onDetailClick = React.useCallback((record: IWorkspaceDetail) => {
    setDetailWorkspaceModalOpen(record.id);
  }, []);

  const onDeleteClick = React.useCallback((record: IWorkspaceDetail) => {
    setDeleteWorkspaceModalOpen(record.id);
  }, []);

  const columns = useTableColumns({ onDetailClick, onDeleteClick, canEdit });

  const handleTableChange = React.useCallback<TTableProps['onChange']>((pagination, sorters) => {
    let sortBy: string | undefined;
    let sortDirection: 'ASC' | 'DESC' | undefined;

    if (sorters.length > 0) {
      sortBy = sorters[0].id;
      sortDirection = sorters[0].desc ? 'DESC' : 'ASC';
    }

    setSearchParams((o) => ({
      ...o,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy,
      direction: sortDirection,
    }));
  }, []);

  return (
    <StyledWorkspaceWrapper>
      <DetailWorkspaceModal
        visible={!!detailWorkspaceModalOpen}
        id={detailWorkspaceModalOpen}
        onRequestClose={() => setDetailWorkspaceModalOpen(undefined)}
      />
      {/* TODO: V2 when delete is ready */}
      {/* <DeleteModal /> */}
      <Table.Header
        title={
          (
            <Typography display="h5" as="span">
              {`${formatMessage({ id: 'departments_title' })} (${data?.pagination.total ?? 0})`}
            </Typography>
          ) as any
        }
        extra={
          canEdit ? (
            <IconButton
              size="medium"
              Icon={PlusOutlined}
              onClick={() => {
                history.push('/business/profile/departments/create-workspace');
              }}
            />
          ) : undefined
        }
      />
      <Table
        loading={isFetching}
        manualSortBy
        disableMultiSort
        manualPagination
        autoResetPage
        columns={columns}
        data={data?.items ?? []}
        initialState={INITIAL_TABLE_STATE}
        pagination={data?.pagination}
        onChange={handleTableChange}
      />
    </StyledWorkspaceWrapper>
  );
};

export default Departments;
